import { BalanceData } from "../data/balance";
import { Pcg32 } from "../maths/pcg32";
import { RoadMap } from "../world/roadMap";
import { Card, CardKind, Tower, sameCard } from "./entities";
import { HandPlan, HandSupply, MIN_CHOICE } from "./handPlan";
import { Offer } from "./offer";
import { OfferKind } from "./entities";

/**
 * Draws card offers from the level RNG stream per DESIGN §4.4. Deterministic.
 *
 * Towers and upgrades are the scarce cards. They are paced rather than rolled, so scarcity is predictable and a run's
 * strength does not hang on dice: the sim marks an in-run offer "towers due" once every N offers (N grows with the
 * towers already standing) and "upgrade due" once every M. A towers-due offer shows `maxTowerCardsPerOffer` DIFFERENT
 * types — the question is which role the defence lacks. Road is paced too: the other slots are road on a road-due
 * offer, which is every offer early in the level and further apart as the road grows (the road never finishes; its
 * pace is what keeps it from growing long). So most offers ask "road, or the tower/upgrade?" rather than handing out
 * everything. What an in-run hand holds is decided when it opens (handPlan.planHand, with the HUD reading the same
 * plan); this class draws the cards.
 *
 * Every offer shown is a real choice (DESIGN §4.4, 2026-09-25, Kevin: "we shouldn't offer duplicates, and we should
 * never offer only 1 option, or offer only to speed up the next card"): its Path cards are different shapes and it has
 * at least MIN_CHOICE road, tower or upgrade cards — a lone card due is topped up with a borrowed one (planHand), or
 * the offer does not show.
 */
/**
 * A shape appears at most once in a hand: three "Straight 3" cards are one choice dealt three times. (One shape's
 * several fits are its ROTATE options once picked, not extra cards.)
 */
const MAX_SAME_PATH = 1;
/** Likewise a tower type: the question a tower hand asks is which role, not how many of one. */
const MAX_SAME_TOWER = 1;

export class OfferGenerator {
  private pathPool: Card[] = [];
  private pathWeights: number[] = [];
  private towerPool: Card[] = [];

  constructor(private readonly balance: BalanceData, private readonly level: number) {}

  /** What the board can take now, for planning an in-run hand (handPlan.planHand). Read-only. */
  supply(road: RoadMap, owned: readonly boolean[], towers: readonly Tower[], canRaise?: (t: Tower) => boolean): HandSupply {
    this.buildPools(road, owned, towers, false);
    // `canRaise` (the sim's canUpgrade) also counts tier 3 → 4 on a type with a branch (sim/branches).
    const upgradeable = towers.some((t) => (canRaise !== undefined ? canRaise(t) : t.alive && t.tier < this.balance.towers[t.archetype].tiers.length));
    return { towerTypes: this.towerPool.length, roadShapes: this.pathPool.length, upgradeable };
  }

  /** Fills the offer's cards. Returns the number drawn (0 when it is no choice: the offer closes unseen). */
  fill(offer: Offer, road: RoadMap, owned: readonly boolean[], towers: readonly Tower[], rng: Pcg32, exclude: readonly Card[] | null): number {
    // M55: the Move card rides an in-run hand's road slots (never the opening draft's, nor a rare's rally offer).
    this.buildPools(road, owned, towers, offer.kind === OfferKind.PostWave && !offer.rare);
    const cards: Card[] = [];
    offer.cards = cards;
    const size = this.balance.cards.offerSize;
    switch (offer.kind) {
      case OfferKind.OpeningPath:
        // A reroll may plan an opening road offer with a tower card beside its road (draftFlow.reroll).
        if (offer.plan !== null) { this.deal(offer.plan, cards, rng, exclude); break; }
        for (let i = 0; i < size; i++) this.push(cards, this.drawPath(cards, rng, exclude));
        // One shape fits: a tower card borrowed from the opening tower offers makes it a choice (taking it is one of
        // those picks made now, and this road offer comes back — draftFlow.placeTower). No shape at all: the offer
        // closes and the opening moves on to its tower offers, three types to choose from.
        if (cards.length === 1 && offer.earlyTowerAllowed) this.push(cards, this.drawTower(cards, rng, exclude));
        break;
      case OfferKind.OpeningTower:
        // Distinct types: the opening choice is WHICH tower, not how many of one. With a single type owned (never on
        // the launch roster, which owns three from the start) a road card beside it makes it a choice: the tower, or
        // one more piece of road in its place.
        for (let i = 0; i < size; i++) this.push(cards, this.drawTower(cards, rng, exclude));
        if (cards.length === 1) this.push(cards, this.drawPath(cards, rng, exclude));
        break;
      default:
        if (offer.plan !== null) this.deal(offer.plan, cards, rng, exclude);
    }
    if (cards.length < MIN_CHOICE) cards.length = 0;
    return cards.length;
  }

  /** Draws an in-run hand to its plan: tower cards, the Upgrade, then road — due and borrowed alike. */
  private deal(plan: HandPlan, cards: Card[], rng: Pcg32, exclude: readonly Card[] | null): void {
    for (let i = 0; i < plan.towers; i++) this.push(cards, this.drawTower(cards, rng, exclude));
    // The one Upgrade card (a reroll that plans one again deals it again: there is no other Upgrade).
    if (plan.upgrade) cards.push({ kind: CardKind.Upgrade, index: 0 });
    for (let i = 0; i < plan.road; i++) this.push(cards, this.drawPath(cards, rng, exclude));
  }

  /**
   * Whether some Path card could be offered now: a shape unlocked, under its per-level cap, with a fit that keeps the
   * road extendable (§4.3). False only once the road has filled the map around its end. Read-only (tools and tests ask it;
   * the hand plan counts the shapes, `supply`).
   */
  roadOpen(road: RoadMap): boolean {
    const shapes = this.balance.cards.shapes;
    for (let i = 0; i < shapes.length; i++) if (this.pathOfferable(road, i)) return true;
    return false;
  }

  private pathOfferable(road: RoadMap, i: number): boolean {
    const s = this.balance.cards.shapes[i];
    if (s.unlockLevel > this.level) return false;
    if (s.maxPerLevel > 0 && road.countSegmentsOfShape(i) >= s.maxPerLevel) return false;
    return road.enumerateValidPlacements(s).length > 0;
  }

  /** Whether the Move card may be dealt: its weight set, enough towers standing (the fortress not counted), a square free. */
  private moveOfferable(road: RoadMap, towers: readonly Tower[]): boolean {
    const m = this.balance.cards.moveCard;
    if (m.weight <= 0) return false;
    let built = 0;
    for (const t of towers) if (t.alive && !this.balance.towers[t.archetype].isBase) built++;
    return built >= m.minTowers && road.collectValidTowerCells().length > 0;
  }

  private push(cards: Card[], card: Card | null): void { if (card !== null) cards.push(card); }

  /**
   * A Path card of a shape not already in the hand (MAX_SAME_PATH), or null when every fitting shape is dealt. On an
   * in-run hand the pool may hold the Move card too (buildPools), so a road slot can deal it instead of a shape.
   */
  private drawPath(current: readonly Card[], rng: Pcg32, exclude: readonly Card[] | null): Card | null {
    return this.draw(current, this.pathPool, this.pathWeights, rng, exclude, MAX_SAME_PATH);
  }

  /** A Tower card of a type not already in the hand (every hand's tower cards are different types). */
  private drawTower(current: readonly Card[], rng: Pcg32, exclude: readonly Card[] | null): Card | null {
    const cap = Math.min(MAX_SAME_TOWER, this.balance.cards.maxSameTowerPerOffer);
    return this.draw(current, this.towerPool, this.towerPool.map(() => this.balance.cards.towerCardWeight), rng, exclude, cap);
  }

  /** Weighted draw from `pool`, skipping excluded cards (unless that empties it) and cards already `cap` times in the offer. */
  private draw(current: readonly Card[], pool: readonly Card[], weights: readonly number[], rng: Pcg32, exclude: readonly Card[] | null, cap: number): Card | null {
    const pick = (useExclude: boolean): Card | null => {
      const cards: Card[] = [], ws: number[] = [];
      for (let i = 0; i < pool.length; i++) {
        const card = pool[i];
        if (useExclude && exclude !== null && exclude.some((e) => sameCard(e, card))) continue;
        if (cap > 0 && current.filter((c) => sameCard(c, card)).length >= cap) continue;
        cards.push(card); ws.push(weights[i]);
      }
      if (cards.length === 0) return null;
      let total = 0;
      for (const w of ws) total += w;
      let r = rng.nextInt(total);
      for (let i = 0; i < cards.length; i++) { r -= ws[i]; if (r < 0) return cards[i]; }
      return cards[cards.length - 1];
    };
    return pick(true) ?? (exclude !== null ? pick(false) : null);
  }

  private buildPools(road: RoadMap, owned: readonly boolean[], towers: readonly Tower[], move: boolean): void {
    this.pathPool = [];
    this.pathWeights = [];
    const shapes = this.balance.cards.shapes;
    for (let i = 0; i < shapes.length; i++) {
      if (!this.pathOfferable(road, i)) continue;
      this.pathPool.push({ kind: CardKind.Path, index: i });
      this.pathWeights.push(shapes[i].weight);
    }
    // M55 (Kevin, 2026-10-21: "a new card for move a tower, which can appear anytime a road card can appear, but only
    // when at least 3 towers are placed"): beside the shapes, in a hand that deals road, while a square is free.
    if (move && this.pathPool.length > 0 && this.moveOfferable(road, towers)) {
      this.pathPool.push({ kind: CardKind.Move, index: 0 });
      this.pathWeights.push(this.balance.cards.moveCard.weight);
    }
    this.towerPool = [];
    const archetypes = this.balance.towers;
    for (let i = 0; i < archetypes.length; i++) {
      if (i >= owned.length || !owned[i] || archetypes[i].isBase) continue;
      if (archetypes[i].onePerMap && towers.some((t) => t.alive && t.archetype === i)) continue;
      this.towerPool.push({ kind: CardKind.Tower, index: i });
    }
  }
}
